import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { formatNumber } from '../../utils/format';

/**
 * Get color based on score value.
 * Red (low) -> Yellow (mid) -> Green (high)
 */
function getScoreColor(score) {
  const s = Math.max(0, Math.min(1, score));

  if (s < 0.4) {
    // Red to Yellow: 0.0-0.4
    const t = s / 0.4;
    const r = 239;
    const g = Math.round(68 + t * (163 - 68));
    const b = Math.round(68 - t * 68);
    return `rgb(${r}, ${g}, ${b})`;
  } else if (s < 0.7) {
    // Yellow to Light Green: 0.4-0.7
    const t = (s - 0.4) / 0.3;
    const r = Math.round(239 - t * (239 - 74));
    const g = Math.round(163 + t * (222 - 163));
    const b = Math.round(0 + t * 128);
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // Light Green to Green: 0.7-1.0
    const t = (s - 0.7) / 0.3;
    const r = Math.round(74 - t * (74 - 22));
    const g = Math.round(222 - t * (222 - 163));
    const b = Math.round(128 - t * (128 - 74));
    return `rgb(${r}, ${g}, ${b})`;
  }
}

/**
 * Visual score indicator component.
 *
 * @param {Object} props
 * @param {number} props.score - Score value between 0 and 1
 * @param {string} props.label - Optional label to display
 * @param {boolean} props.showLabel - Show numeric label on the bar
 * @param {'small'|'medium'|'large'} props.size - Size variant
 * @param {string} props.tooltipText - Custom tooltip text
 * @param {Object} props.sx - Additional styles
 */
export default function RecommendationScoreBar({
  score = 0,
  label,
  showLabel = true,
  size = 'medium',
  tooltipText,
  sx = {},
}) {
  const normalizedScore = Math.max(0, Math.min(1, score));
  const percentage = Math.round(normalizedScore * 100);
  const color = getScoreColor(normalizedScore);

  const heights = {
    small: 6,
    medium: 10,
    large: 14,
  };

  const height = heights[size] || heights.medium;

  const tooltip = tooltipText || `Score: ${formatNumber(normalizedScore)} (${percentage}%)`;

  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: 80,
          ...sx,
        }}
      >
        {/* Score bar container */}
        <Box
          sx={{
            flex: 1,
            height,
            bgcolor: 'action.hover',
            borderRadius: height / 2,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* Filled portion */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: `${percentage}%`,
              bgcolor: color,
              borderRadius: height / 2,
              transition: 'width 0.3s ease, background-color 0.3s ease',
            }}
          />
        </Box>

        {/* Numeric label */}
        {showLabel && (
          <Typography
            variant="caption"
            sx={{
              minWidth: 36,
              textAlign: 'right',
              fontWeight: 500,
              color: 'text.secondary',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {label || formatNumber(normalizedScore)}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

/**
 * Compact inline score display.
 */
export function ScoreChip({ score, label, color: customColor }) {
  const normalizedScore = Math.max(0, Math.min(1, score));
  const color = customColor || getScoreColor(normalizedScore);

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: 1,
        bgcolor: `${color}20`,
        border: `1px solid ${color}40`,
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: color,
        }}
      />
      <Typography
        variant="caption"
        sx={{
          fontWeight: 500,
          color: 'text.primary',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {label || formatNumber(normalizedScore)}
      </Typography>
    </Box>
  );
}
